import React from 'react';
import { motion } from 'framer-motion';
import { Target, Compass, BookOpen, GraduationCap, Building2, BookOpenIcon } from 'lucide-react';
import LazyImage from '../common/LazyImage';
import aboutImg from '../../assets/skyford_excellence_girls.jpeg';

const About = () => {
  const stats = [
    { label: "IATA Authorized", icon: <BookOpen className="w-6 h-6" /> },
    { label: "Expert Faculty", icon: <GraduationCap className="w-6 h-6" /> },
    { label: "Modern Campuses", icon: <Building2 className="w-6 h-6" /> },
    { label: "Advanced Curriculum", icon: <BookOpenIcon className="w-6 h-6" /> },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[80vh] bg-gradient-to-br from-red-950 via-red-900 to-red-800 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(-45deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:40px_40px]" />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative max-w-7xl mx-auto px-4 py-20"
        >
          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-8"
              style={{ lineHeight: '1.5' }}
            >
              Shape Your Future in <br /> Aviation
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-md sm:text-lg text-red-100 mb-12"
            >
              SKYFORD - A trusted name in aviation education, authorized by IATA and approved by the Government of India and Kerala.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-white"
              >
                {stat.icon}
                <p className="mt-2 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Excellence Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4">

          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-semibold mb-5 sm:mb-8 text-center"
          >
            Excellence in Aviation Education
          </motion.h2>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 sm:mb-14"
          >
            <div className="rounded-2xl overflow-hidden">
              <LazyImage 
                src={aboutImg}
                alt="SKYFORD Excellence"
                className="w-full h-64 sm:h-80 md:h-96"
              />
            </div>
          </motion.div>

          {/* 2-Column Text Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6"
          >
            <div className="space-y-6">
              <p className="text-gray-600 leading-relaxed">
                Skyford Aviation is a premier aviation training institute dedicated to shaping the next generation of aviation professionals. With a strong presence across Calicut, Cochin, Thrissur, Tirur, and Perinthalmanna, Skyford has established itself as a trusted name in aviation education, known for its commitment to excellence and industry-focused training.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We offer specialized programs including the BBA in Aviation, designed to equip students with in-depth knowledge and practical skills required to succeed in the dynamic aviation and travel industry. As an authorized training center for globally recognized reservation systems such as Galileo, Amadeus, and Sabre, we ensure our students gain hands-on experience with the tools used by leading airlines and travel companies worldwide.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Skyford Aviation is proudly accredited by IATA and the STED Council, reflecting our adherence to international standards in aviation training. We are also in academic partnership with SEGi University, Malaysia, providing students with global exposure and enhanced academic pathways.
              </p>
            </div>
            <div className="space-y-6">
              <p className="text-gray-600 leading-relaxed">
                Our curriculum is carefully crafted to bridge the gap between academic learning and industry requirements, supported by experienced faculty and modern training facilities.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At Skyford, we believe in empowering students beyond the classroom. Our strong placement support system connects students with leading airlines, airports, and travel organizations, helping them launch successful careers in aviation and allied sectors.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Driven by innovation, professionalism, and a passion for aviation, Skyford Aviation continues to inspire and guide aspiring professionals towards a future in the skies.
              </p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className=" relative">
        <div className="absolute inset-0  pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            <div className=" p-5 sm:p-8 rounded-3xl border border-gray-100 bg-gray-50">

              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <Target className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-red-900">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our instructors focus on building the skills, knowledge and attitude of students during all phases of the course as a strategy to fulfill future aviation, hospitality and travel industry requirements. Committed to promote International standards in all our endeavors like academic excellence, teaching-learning process and providing higher education through equitable access.
              </p>
            </div>

            <div className=" p-5 sm:p-8 rounded-3xl border border-gray-100 bg-gray-50">

              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <Compass className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-red-900">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our vision is to lead and set the highest standards of aviation, hospitality, travel & tourism training and to be a preferred Aviation Training Academy in India. To bridge the gap between the demand and supply of trained and skilled professionals in the competitive industry.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
